import type { EligibilityBenefits } from '@features/patient-coverages/types';
import { benefitCents, benefitPercent } from '@features/patient-coverages/utils/patientCost';

export interface FinancialFieldDef {
  field:
    | 'copayment'
    | 'coinsurance'
    | 'deductible'
    | 'remaining_deductible'
    | 'oop_max'
    | 'remaining_oop_max';
  label: string;
  prefix: '$' | '%';
  /** Key used in the submitted financials patch. */
  apiKey: keyof EligibilityBenefits;
}

export const FINANCIAL_FIELDS: FinancialFieldDef[] = [
  { field: 'copayment', label: 'Copay', prefix: '$', apiKey: 'copayment_cents' },
  { field: 'coinsurance', label: 'Coinsurance', prefix: '%', apiKey: 'coinsurance_percent' },
  { field: 'deductible', label: 'Deductible', prefix: '$', apiKey: 'deductible_cents' },
  {
    field: 'remaining_deductible',
    label: 'Remaining deductible',
    prefix: '$',
    apiKey: 'remaining_deductible_cents',
  },
  { field: 'oop_max', label: 'Out-of-pocket max', prefix: '$', apiKey: 'oop_max_cents' },
  {
    field: 'remaining_oop_max',
    label: 'Remaining OOP max',
    prefix: '$',
    apiKey: 'remaining_oop_max_cents',
  },
];

/** Fields rendered above the "+ More fields" fold. */
export const PRIMARY_FIELDS: FinancialFieldDef['field'][] = ['copayment', 'coinsurance'];

export function centsToDollars(cents: unknown): string {
  const amount = benefitCents(cents);
  return amount === null ? '' : (amount / 100).toFixed(2);
}

/** Seed the form's input strings from a snapshot's financials dict. */
export function seedFinancialInputs(financials: Record<string, unknown>): Record<string, string> {
  const seeded: Record<string, string> = {};
  for (const def of FINANCIAL_FIELDS) {
    if (def.prefix === '$') {
      seeded[def.field] = centsToDollars(financials[def.apiKey]);
    } else {
      const pct = benefitPercent(financials);
      seeded[def.field] = pct !== null ? String(pct) : '';
    }
  }
  return seeded;
}

/**
 * Parse one input's raw string. `null` means empty (a clear when the field
 * was seeded); `undefined` means unparsable or negative — treat as untouched.
 */
function parseInput(def: FinancialFieldDef, raw: string): number | null | undefined {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || (def.prefix === '%' && n > 100)) return undefined;
  const amount = Math.round(n * 100);
  return def.prefix === '$' ? (Number.isSafeInteger(amount) ? amount : undefined) : n;
}

/**
 * Diff the form state against its seeded values into the submitted patch.
 * Returns `null` when nothing changed.
 *
 */
export function buildFinancialsPatch(
  initial: Record<string, string>,
  current: Record<string, string>,
  assertedFields?: ReadonlySet<FinancialFieldDef['field']>,
): EligibilityBenefits | null {
  const patch: EligibilityBenefits = {};
  for (const def of FINANCIAL_FIELDS) {
    const before = parseInput(def, initial[def.field] ?? '');
    const after = parseInput(def, current[def.field] ?? '');
    if (after === undefined) continue; // unparsable — treat as untouched
    if (assertedFields) {
      if (!assertedFields.has(def.field)) continue;
    } else if (before === after) {
      continue;
    }
    patch[def.apiKey] = after;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * Label of the first edited field whose value cannot be submitted (negative
 * or unparsable), or null when every non-empty input is valid. Submission
 * must be blocked on a non-null result: silently dropping an invalid entry
 * would recreate the "override appeared to do nothing" failure mode.
 */
export function firstInvalidFinancialInput(current: Record<string, string>): string | null {
  for (const def of FINANCIAL_FIELDS) {
    const raw = (current[def.field] ?? '').trim();
    if (raw === '') continue;
    if (parseInput(def, raw) === undefined) return def.label;
  }
  return null;
}
